import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Navigation from '../components/Navigation';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ArrowLeft, Edit, User, Heart, Calendar, FileText, Phone, Mail, MapPin, AlertCircle } from 'lucide-react';
import axios from 'axios';
import { API } from '../App';
import { toast } from 'sonner';

const PatientDetail = ({ user, setUser }) => {
  const { patientId } = useParams();
  const navigate = useNavigate();
  const [patient, setPatient] = useState(null);
  const [appointments, setAppointments] = useState([]);
  const [medicalHistory, setMedicalHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user.role !== 'doctor') {
      toast.error('Only doctors can view patient details');
      navigate('/dashboard');
      return;
    }
    loadPatientData();
  }, [patientId]);

  const loadPatientData = async () => {
    try {
      const [patientRes, appointmentsRes, historyRes] = await Promise.all([
        axios.get(`${API}/patients/${patientId}`),
        axios.get(`${API}/patients/${patientId}/appointments`),
        axios.get(`${API}/patients/${patientId}/medical-history`)
      ]);
      
      setPatient(patientRes.data);
      setAppointments(appointmentsRes.data);
      setMedicalHistory(historyRes.data);
    } catch (error) {
      toast.error('Failed to load patient data');
      navigate('/patients');
    } finally {
      setLoading(false);
    }
  };

  const calculateAge = (dob) => {
    const birthDate = new Date(dob);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-cyan-50 via-white to-teal-50">
        <Navigation user={user} setUser={setUser} />
        <div className="flex items-center justify-center h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-b-4 border-teal-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-cyan-50 via-white to-teal-50">
      <Navigation user={user} setUser={setUser} />
      
      <div className="container mx-auto px-4 py-8 sm:py-8 pt-24 ">
        <div className="flex justify-between items-center mb-6">
          <Button
            onClick={() => navigate('/patients')}
            variant="outline"
            className="flex items-center gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Patients
          </Button>
          <Button
            onClick={() => navigate(`/patients/${patientId}/edit`)}
            className="bg-gradient-to-r from-teal-600 to-cyan-600 text-white flex items-center gap-2"
          >
            <Edit className="w-4 h-4" />
            Edit Patient
          </Button>
        </div>

        {/* Patient Header */}
        <Card className="p-8 bg-white shadow-xl mb-8">
          <div className="flex items-start gap-6">
            <div className="w-24 h-24 rounded-full bg-gradient-to-br from-teal-500 to-cyan-600 flex items-center justify-center text-white text-4xl font-bold">
              {patient.full_name.charAt(0).toUpperCase()}
            </div>
            <div className="flex-1">
              <h1 className="text-3xl font-bold text-gray-800 mb-2">{patient.full_name}</h1>
              <div className="grid md:grid-cols-3 gap-4 text-gray-600">
                <div className="flex items-center gap-2">
                  <User className="w-4 h-4" />
                  <span>{calculateAge(patient.date_of_birth)} years, {patient.gender}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Heart className="w-4 h-4" />
                  <span>Blood Group: {patient.blood_group || 'N/A'}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  <span>DOB: {new Date(patient.date_of_birth).toLocaleDateString()}</span>
                </div>
              </div>
            </div>
          </div>
        </Card>

        {/* Tabs */}
        <Tabs defaultValue="overview" className="space-y-6">
          <TabsList className="bg-white shadow-md p-1 rounded-lg">
            <TabsTrigger value="overview" className="px-6">Overview</TabsTrigger>
            <TabsTrigger value="appointments" className="px-6">Appointments</TabsTrigger>
            <TabsTrigger value="medical-history" className="px-6">Medical History</TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-6">
            <div className="grid md:grid-cols-2 gap-6">
              <Card className="p-6 bg-white shadow-lg">
                <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
                  <Phone className="w-5 h-5 text-teal-600" />
                  Contact Information
                </h2>
                <div className="space-y-3">
                  <div>
                    <p className="text-sm text-gray-500">Email</p>
                    <p className="text-gray-800 flex items-center gap-2">
                      <Mail className="w-4 h-4" />
                      {patient.email}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Phone</p>
                    <p className="text-gray-800 flex items-center gap-2">
                      <Phone className="w-4 h-4" />
                      {patient.phone}
                    </p>
                  </div>
                  {patient.address && (
                    <div>
                      <p className="text-sm text-gray-500">Address</p>
                      <p className="text-gray-800 flex items-start gap-2">
                        <MapPin className="w-4 h-4 mt-1" />
                        <span>
                          {patient.address}
                          {patient.city && `, ${patient.city}`}
                          {patient.state && `, ${patient.state}`}
                        </span>
                      </p>
                    </div>
                  )}
                </div>
              </Card>

              <Card className="p-6 bg-white shadow-lg">
                <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
                  <AlertCircle className="w-5 h-5 text-red-600" />
                  Emergency Contact
                </h2>
                {patient.emergency_contact_name ? (
                  <div className="space-y-3">
                    <div>
                      <p className="text-sm text-gray-500">Name</p>
                      <p className="text-gray-800">{patient.emergency_contact_name}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Phone</p>
                      <p className="text-gray-800">{patient.emergency_contact_phone}</p>
                    </div>
                  </div>
                ) : (
                  <p className="text-gray-500">No emergency contact information</p>
                )}
              </Card>

              <Card className="p-6 bg-white shadow-lg">
                <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
                  <FileText className="w-5 h-5 text-blue-600" />
                  Insurance Information
                </h2>
                {patient.insurance_provider ? (
                  <div className="space-y-3">
                    <div>
                      <p className="text-sm text-gray-500">Provider</p>
                      <p className="text-gray-800">{patient.insurance_provider}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Policy Number</p>
                      <p className="text-gray-800">{patient.insurance_number}</p>
                    </div>
                  </div>
                ) : (
                  <p className="text-gray-500">No insurance information</p>
                )}
              </Card>

              <Card className="p-6 bg-white shadow-lg">
                <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
                  <Heart className="w-5 h-5 text-pink-600" />
                  Medical Information
                </h2>
                <div className="space-y-3">
                  {patient.allergies && (
                    <div>
                      <p className="text-sm text-gray-500">Allergies</p>
                      <p className="text-gray-800">{patient.allergies}</p>
                    </div>
                  )}
                  {patient.chronic_conditions && (
                    <div>
                      <p className="text-sm text-gray-500">Chronic Conditions</p>
                      <p className="text-gray-800">{patient.chronic_conditions}</p>
                    </div>
                  )}
                  {patient.current_medications && (
                    <div>
                      <p className="text-sm text-gray-500">Current Medications</p>
                      <p className="text-gray-800">{patient.current_medications}</p>
                    </div>
                  )}
                  {!patient.allergies && !patient.chronic_conditions && !patient.current_medications && (
                    <p className="text-gray-500">No medical information recorded</p>
                  )}
                </div>
              </Card>
            </div>

            {patient.notes && (
              <Card className="p-6 bg-white shadow-lg">
                <h2 className="text-xl font-bold text-gray-800 mb-4">Notes</h2>
                <p className="text-gray-700 whitespace-pre-wrap">{patient.notes}</p>
              </Card>
            )}
          </TabsContent>

          {/* Appointments Tab */}
          <TabsContent value="appointments">
            <Card className="p-6 bg-white shadow-lg">
              <h2 className="text-2xl font-bold text-gray-800 mb-6">Appointment History</h2>
              {appointments.length === 0 ? (
                <p className="text-gray-500 text-center py-8">No appointments found</p>
              ) : (
                <div className="space-y-4">
                  {appointments.map((apt) => (
                    <div key={apt.id} className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="font-semibold text-gray-800">{apt.appointment_date} at {apt.appointment_time}</p>
                          <p className="text-sm text-gray-600 mt-1">Type: {apt.type}</p>
                        </div>
                        <span className={`px-3 py-1 rounded-full text-sm font-semibold ${
                          apt.status === 'scheduled' ? 'bg-green-100 text-green-700' :
                          apt.status === 'completed' ? 'bg-blue-100 text-blue-700' :
                          'bg-red-100 text-red-700'
                        }`}>
                          {apt.status}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Card>
          </TabsContent>

          {/* Medical History Tab */}
          <TabsContent value="medical-history">
            <Card className="p-6 bg-white shadow-lg">
              <h2 className="text-2xl font-bold text-gray-800 mb-6">Medical History</h2>
              {medicalHistory.length === 0 ? (
                <p className="text-gray-500 text-center py-8">No medical history recorded</p>
              ) : (
                <div className="space-y-4">
                  {medicalHistory.map((record) => (
                    <div key={record.id} className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                      <div className="flex justify-between items-start mb-2">
                        <h3 className="font-semibold text-gray-800">{record.diagnosis}</h3>
                        <span className="text-sm text-gray-500">{record.date}</span>
                      </div>
                      <p className="text-sm text-gray-600">Doctor: {record.doctor_name}</p>
                      {record.notes && (
                        <p className="text-sm text-gray-700 mt-2">{record.notes}</p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default PatientDetail;
